/* variant-sku-media-filter.js
   SKU/alt media filter - scoped to gallery root + slider refresh.
   Place in Assets and include with:
     <script type="module" src="{{ 'variant-sku-media-filter.js' | asset_url }}"></script>
*/

const DEBUG = false; // set true to see logs

const normalize = (s = '') =>
  String(s || '')
    .toLowerCase()
    .trim()
    .replace(/[_\u2011\-\s]+/g, ' ')
    .replace(/[^a-z0-9 ]+/g, '')
    .replace(/\s+/g, ' ')
    .trim();

const debounce = (fn, wait = 80) => {
  let t;
  return (...a) => {
    clearTimeout(t);
    t = setTimeout(() => fn(...a), wait);
  };
};

function log(...args) { if (DEBUG) console.debug('[vfg]', ...args); }

/**
 * Try to find the gallery root that belongs to the current product/section.
 * Strategy:
 *  - prefer nearest variant-picker / product-form and look within that shopify section
 *  - fallback to common gallery selectors on the page
 */
function findGalleryRoot() {
  // find the variant picker or product form that represents the product area
  const picker = document.querySelector('variant-picker, .product-form, form[action*="/cart/add"], [data-product-json], [data-product-id]');
  const section = picker ? picker.closest('.shopify-section, section, [data-section-id], .product, .product-single, .product-information') : null;

  const selectors = [
    '.product-information__media',
    '.product-media-gallery',
    '.product-gallery',
    '.media-gallery',
    '.slideshow-container',
    '.gallery',
    '[data-product-media]'
  ];

  // search inside the section first
  if (section) {
    for (const sel of selectors) {
      const el = section.querySelector(sel);
      if (el) {
        log('gallery root found in section via', sel);
        return el;
      }
    }
  }

  // fallback: search whole document
  for (const sel of selectors) {
    const el = document.querySelector(sel);
    if (el) {
      log('gallery root found via', sel);
      return el;
    }
  }

  // final fallback
  return document;
}

/**
 * Read the currently-selected token (sku / value). Robust fallbacks for many themes.
 */
function readSelectedToken() {
  // variant-picker radios / swatches
  const radio =
    document.querySelector('variant-picker input[type="radio"][checked]') ||
    document.querySelector('variant-picker input[type="radio"]:checked') ||
    document.querySelector('.swatch input:checked') ||
    document.querySelector('.swatch__item.is-active input:checked') ||
    document.querySelector('input[data-option-value-id]:checked') ||
    document.querySelector('input[name="id"]:checked') ||
    null;

  if (radio) {
    if (radio.dataset && (radio.dataset.sku || radio.dataset.optionValue || radio.dataset.optionValueId)) {
      return radio.dataset.sku || radio.dataset.optionValue || radio.dataset.optionValueId;
    }
    if (radio.value) return radio.value;
    if (radio.getAttribute) {
      return (
        radio.getAttribute('aria-label') ||
        radio.getAttribute('data-value') ||
        radio.getAttribute('data-option-value') ||
        radio.value ||
        null
      );
    }
  }

  // active swatch element
  const activeSw = document.querySelector('[data-swatch][aria-pressed="true"], .swatch__item.is-active, .swatch__item.active');
  if (activeSw) {
    if (activeSw.dataset && (activeSw.dataset.sku || activeSw.dataset.value)) return activeSw.dataset.sku || activeSw.dataset.value;
    if (activeSw.textContent) return activeSw.textContent.trim();
  }

  // select dropdown fallback
  const sel = document.querySelector('variant-picker select, select[name="id"], select[name="variant"], select#ProductSelect-product-template');
  if (sel && sel.selectedIndex >= 0) {
    const opt = sel.options[sel.selectedIndex];
    if (opt) {
      if (opt.dataset && (opt.dataset.sku || opt.dataset.optionValueId)) return opt.dataset.sku || opt.dataset.optionValueId;
      if (opt.text) return opt.text.trim();
      if (opt.value) return opt.value;
    }
  }

  // an on-page SKU element
  const skuEl = document.querySelector('[data-product-sku], .product-sku, .sku, .product__sku, [data-sku], .variant-sku');
  if (skuEl && skuEl.textContent) return skuEl.textContent.trim();

  // maybe theme includes product JSON script
  const jsonScript = document.querySelector('variant-picker script[type="application/json"], script#ProductJson-product-template, script[data-product-json]');
  if (jsonScript && jsonScript.textContent) {
    try {
      const parsed = JSON.parse(jsonScript.textContent);
      if (parsed && parsed.variants && Array.isArray(parsed.variants)) {
        const selected = parsed.variants.find(v => v.available || v.selected || v.id === Number(new URLSearchParams(window.location.search).get('variant')));
        if (selected && selected.sku) return selected.sku;
      }
    } catch (e) { /* ignore */ }
  }

  // url param fallback
  const params = new URLSearchParams(window.location.search);
  const variantId = params.get('variant');
  if (variantId) return variantId;

  return null;
}

/**
 * Refresh known slider libraries inside a given root element so pagination / dots update.
 */
function refreshSliders(root) {
  try {
    // Flickity
    const flickEl = root.querySelector('.flickity-enabled');
    if (flickEl && flickEl.flkty && typeof flickEl.flkty.reloadCells === 'function') {
      flickEl.flkty.reloadCells();
      flickEl.flkty.resize();
    }
    // Splide
    const splideEl = root.querySelector('.splide');
    if (splideEl && splideEl.splide && typeof splideEl.splide.refresh === 'function') splideEl.splide.refresh();
    // Swiper
    const swiperEl = root.querySelector('.swiper-container, .swiper');
    if (swiperEl && swiperEl.swiper && typeof swiperEl.swiper.update === 'function') swiperEl.swiper.update();
    // Glide
    const glideEl = root.querySelector('[data-glide-el]');
    if (glideEl && glideEl._glide && typeof glideEl._glide.mount === 'function') glideEl._glide.mount();
  } catch (e) {
    // ignore errors
  }
}

/**
 * Apply the filter within a particular root (gallery scope).
 * Hides non-matching slides (display:none) and refreshes slider libs.
 */
function applyFilter(root = null, tokenOverride = null) {
  const galleryRoot = root || findGalleryRoot();
  if (!galleryRoot) return;

  const rawToken = tokenOverride != null ? tokenOverride : readSelectedToken();
  const tokenNorm = normalize(rawToken || '');
  log('applyFilter token:', rawToken, '->', tokenNorm);

  // Candidate slide selectors (only inside galleryRoot)
  const selectors = [
    '.product-media-container',
    '.slideshow__slide',
    '.splide__slide',
    '.splide__track > li',
    '.slide',
    '.gallery-item',
    '.media-item',
    '.product-media',
    'li[id^="product-"]'
  ];

  const slides = [];
  selectors.forEach(sel => {
    Array.from(galleryRoot.querySelectorAll(sel)).forEach(node => {
      if (!node) return;
      if (slides.includes(node)) return;
      slides.push(node);
    });
  });

  if (!slides.length) {
    log('no slides found in galleryRoot');
    return;
  }

  let visible = [];

  if (!tokenNorm) {
    visible = slides.slice();
  } else {
    slides.forEach(s => {
      const ds = (s.dataset && (s.dataset.sku || s.dataset.alt || s.dataset.mediaAlt)) || s.getAttribute && (s.getAttribute('data-sku') || s.getAttribute('data-alt')) || '';
      const itemNorm = normalize(ds || (s.textContent || ''));
      if (!itemNorm) return;
      if (itemNorm === tokenNorm || itemNorm.includes(tokenNorm) || tokenNorm.includes(itemNorm)) {
        visible.push(s);
        return;
      }
      const words = itemNorm.split(' ');
      if (words.includes(tokenNorm)) visible.push(s);
    });
  }

  if (visible.length === 0) {
    // fallback: show all if nothing matches
    visible = slides.slice();
    log('no match -> fallback show all');
  } else {
    log('visible slides count:', visible.length, 'of', slides.length);
  }

  slides.forEach(s => {
    try {
      const show = visible.includes(s);
      s.style.display = show ? '' : 'none';
      if (show) s.removeAttribute('aria-hidden'); else s.setAttribute('aria-hidden', 'true');
    } catch (e) { /* ignore */ }
  });

  // refresh slider libraries so native pagination updates
  refreshSliders(galleryRoot);
}

/* expose for manual debug */
window.applyVariantSkuFilter = (root = null, token = null) => applyFilter(root, token);

const debouncedApply = debounce(() => applyFilter(null), 90);

/**
 * Wire up controls and theme variant events.
 */
function wireControls() {
  const selectors = [
    'variant-picker input',
    'variant-picker select',
    '.swatch input',
    '.swatch__item',
    '[data-swatch]',
    'input[data-option-value-id]',
    'input[name="id"]',
    '.variant-option__button-label',
    '.product-form__input' // maybe theme specific
  ];
  document.querySelectorAll(selectors.join(',')).forEach(el => {
    if (el._vfgBound) return;
    el.addEventListener('change', () => setTimeout(debouncedApply, 40));
    el.addEventListener('click', () => setTimeout(debouncedApply, 40));
    el._vfgBound = true;
  });

  // common theme events
  ['variantupdate','variant:change','product:variant:change','variantUpdate','VariantUpdateEvent','variantchanged','variantchange','productVariantChange','theme:variant:changed'].forEach(evt =>
    document.addEventListener(evt, () => setTimeout(debouncedApply, 40))
  );

  // catch selects changes (some themes)
  document.addEventListener('change', e => {
    const t = e.target;
    if (t && (t.matches && (t.matches('select[name="id"], select[name="variant"], select') || t.matches('input[name="id"]')))) {
      setTimeout(debouncedApply, 40);
    }
  }, true);
}

/**
 * Observe DOM changes inside the product/variant area and gallery root.
 */
function observe() {
  const pickRoot = document.querySelector('variant-picker') || document.body;
  const mo = new MutationObserver(debounce(() => {
    log('mutation on picker root -> wire + apply');
    wireControls();
    debouncedApply();
  }, 140));
  mo.observe(pickRoot, { childList: true, subtree: true, attributes: true });

  const galleryRoot = findGalleryRoot() || document.body;
  const mo2 = new MutationObserver(debounce(() => {
    log('mutation on gallery root -> apply');
    debouncedApply();
  }, 140));
  mo2.observe(galleryRoot, { childList: true, subtree: true, attributes: true });
}

/* polling fallback to detect variant change */
let lastToken = null;
let pollingInterval = null;
function startPolling() {
  if (pollingInterval) return;
  pollingInterval = setInterval(() => {
    try {
      const token = readSelectedToken();
      if (token !== lastToken) {
        log('token changed (poll):', lastToken, '=>', token);
        lastToken = token;
        debouncedApply();
      }
    } catch (e) {}
  }, 220);
}
function stopPolling() { if (pollingInterval) { clearInterval(pollingInterval); pollingInterval = null; } }

function init() {
  wireControls();
  observe();
  debouncedApply();
  startPolling();
  // extra run for lazy-loaded widgets
  setTimeout(debouncedApply, 300);
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') debouncedApply();
  });
  window.addEventListener('beforeunload', stopPolling);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init);
} else {
  init();
}

export {}; // keep module scope
